/**
 * PDF list marker padding for MaisonNeue-Book at 8pt. Update ADVANCE_PT_BY_CHAR if the face or size changes.
 */

const ORDERED_ITEM_MAX = 99;

const HAIR_SPACE = '\u200a';
const THIN_SPACE = '\u2009';
const BULLET_MARKER = '• ';

const ADVANCE_PT_BY_CHAR: Readonly<Record<string, number>> = {
  '0': 5.152,
  '1': 2.968,
  '2': 4.768,
  '3': 4.704,
  '4': 4.48,
  '5': 4.68,
  '6': 4.912,
  '7': 4.016,
  '8': 4.528,
  '9': 4.72,
  '.': 2.408,
  '•': 2.864,
  ' ': 2.16,
  [HAIR_SPACE]: 0.6,
  [THIN_SPACE]: 1.2,
};

function stringWidthPt(text: string) {
  let widthPt = 0;

  for (const character of text) {
    const advancePt = ADVANCE_PT_BY_CHAR[character];
    if (advancePt === undefined) {
      throw new Error(
        `list-item-marker-metrics: no advance for ${JSON.stringify(character)}`,
      );
    }
    widthPt += advancePt;
  }

  return widthPt;
}

function pickOrderedListNumberBodyGap() {
  const halfNormalSpaceWidthPt = stringWidthPt(' ') / 2;
  let bestGap = THIN_SPACE;
  let bestGapErrorPt = Math.abs(
    stringWidthPt(THIN_SPACE) - halfNormalSpaceWidthPt,
  );

  function considerGapCandidate(candidate: string) {
    const errorPt = Math.abs(stringWidthPt(candidate) - halfNormalSpaceWidthPt);
    if (errorPt < bestGapErrorPt - 0.0001) {
      bestGapErrorPt = errorPt;
      bestGap = candidate;
    }
  }

  considerGapCandidate('');
  considerGapCandidate(HAIR_SPACE);
  considerGapCandidate(THIN_SPACE);

  return bestGap;
}

export const ORDERED_LIST_NUMBER_BODY_GAP = pickOrderedListNumberBodyGap();

function orderedMarkerCoreWidthPt(itemNumber: number) {
  return stringWidthPt(`${String(itemNumber)}.` + ORDERED_LIST_NUMBER_BODY_GAP);
}

const hairSpaceWidthPt = stringWidthPt(HAIR_SPACE);
const thinSpaceWidthPt = stringWidthPt(THIN_SPACE);

function fillSuffixForSlack(slackWidthPt: number) {
  if (slackWidthPt <= 0.001) {
    return '';
  }

  let slackFill = '';
  let slackFillWidthPt = 0;

  while (slackWidthPt - slackFillWidthPt >= hairSpaceWidthPt - 0.0001) {
    slackFill += HAIR_SPACE;
    slackFillWidthPt += hairSpaceWidthPt;
  }

  const remainderAfterHairsPt = slackWidthPt - slackFillWidthPt;
  if (
    remainderAfterHairsPt > 0.0005 &&
    thinSpaceWidthPt <= remainderAfterHairsPt + thinSpaceWidthPt * 0.35
  ) {
    slackFill += THIN_SPACE;
  }

  return slackFill;
}

let markerColumnWidthPt = 0;

for (let itemNumber = 1; itemNumber <= ORDERED_ITEM_MAX; itemNumber += 1) {
  markerColumnWidthPt = Math.max(
    markerColumnWidthPt,
    orderedMarkerCoreWidthPt(itemNumber),
  );
}

function centeredLeadTailForCore(markerCore: string) {
  const markerCoreWidthPt = stringWidthPt(markerCore);
  const slackWidthPt = Math.max(0, markerColumnWidthPt - markerCoreWidthPt);
  const slackFill = fillSuffixForSlack(slackWidthPt);

  let leadFill = '';
  let tailFill = '';

  if (slackFill.length > 0) {
    const slackFillWidthPt = stringWidthPt(slackFill);
    const targetLeadFillWidthPt = slackFillWidthPt / 2;
    let leadAccumulatedWidthPt = 0;
    let splitIndex = 0;

    for (; splitIndex < slackFill.length; splitIndex += 1) {
      const glyphWidthPt = stringWidthPt(slackFill[splitIndex]);
      if (
        leadAccumulatedWidthPt + glyphWidthPt <=
        targetLeadFillWidthPt + 0.0001
      ) {
        leadFill += slackFill[splitIndex];
        leadAccumulatedWidthPt += glyphWidthPt;
      } else {
        break;
      }
    }

    tailFill = slackFill.slice(splitIndex);
  }

  function fullPrefixWidthPt() {
    return stringWidthPt(leadFill + markerCore + tailFill);
  }

  while (true) {
    const prefixWidthPt = fullPrefixWidthPt();
    if (prefixWidthPt >= markerColumnWidthPt - 0.0001) {
      break;
    }

    const prefixWidthIfAppendHairPt = stringWidthPt(
      leadFill + markerCore + tailFill + HAIR_SPACE,
    );

    if (prefixWidthIfAppendHairPt <= markerColumnWidthPt + 0.00015) {
      tailFill += HAIR_SPACE;
      continue;
    }

    const undershootPt = markerColumnWidthPt - prefixWidthPt;
    const overshootIfHairPt = prefixWidthIfAppendHairPt - markerColumnWidthPt;

    if (undershootPt > overshootIfHairPt) {
      tailFill += HAIR_SPACE;
    }

    break;
  }

  let remainderPt = markerColumnWidthPt - fullPrefixWidthPt();

  if (
    remainderPt > 0.0005 &&
    thinSpaceWidthPt <= remainderPt + thinSpaceWidthPt * 0.35
  ) {
    tailFill += THIN_SPACE;
  }

  remainderPt = markerColumnWidthPt - fullPrefixWidthPt();

  if (remainderPt < -0.0001 && tailFill.endsWith(THIN_SPACE)) {
    tailFill = tailFill.slice(0, -THIN_SPACE.length);
  }

  return { lead: leadFill, tail: tailFill };
}

const ORDERED_LIST_MARKER_PREFIX_LEAD: Array<string> = [];
const ORDERED_LIST_MARKER_PREFIX_TAIL: Array<string> = [];

for (let itemNumber = 1; itemNumber <= ORDERED_ITEM_MAX; itemNumber += 1) {
  const orderedMarkerCore =
    `${String(itemNumber)}.` + ORDERED_LIST_NUMBER_BODY_GAP;
  const { lead, tail } = centeredLeadTailForCore(orderedMarkerCore);

  ORDERED_LIST_MARKER_PREFIX_LEAD.push(lead);
  ORDERED_LIST_MARKER_PREFIX_TAIL.push(tail);
}

export const ORDERED_LIST_MARKER_COLUMN_WIDTH_PT =
  Math.ceil(markerColumnWidthPt * 1000) / 1000;

const { lead: unorderedLeadFill, tail: unorderedTailFill } =
  centeredLeadTailForCore(BULLET_MARKER);
const unorderedListPrefixString =
  unorderedLeadFill + BULLET_MARKER + unorderedTailFill;

export function orderedListMarkerPrefix(itemNumber: number) {
  const zeroBasedIndex = itemNumber - 1;
  const lastIndex = ORDERED_LIST_MARKER_PREFIX_LEAD.length - 1;

  const leadFill =
    zeroBasedIndex >= 0 &&
    zeroBasedIndex < ORDERED_LIST_MARKER_PREFIX_LEAD.length
      ? ORDERED_LIST_MARKER_PREFIX_LEAD[zeroBasedIndex]
      : ORDERED_LIST_MARKER_PREFIX_LEAD[lastIndex];

  const tailFill =
    zeroBasedIndex >= 0 &&
    zeroBasedIndex < ORDERED_LIST_MARKER_PREFIX_TAIL.length
      ? ORDERED_LIST_MARKER_PREFIX_TAIL[zeroBasedIndex]
      : ORDERED_LIST_MARKER_PREFIX_TAIL[lastIndex];

  return (
    leadFill +
    `${String(itemNumber)}.` +
    ORDERED_LIST_NUMBER_BODY_GAP +
    tailFill
  );
}

export const unorderedListMarkerPrefix = () => unorderedListPrefixString;
